import * as THREE from 'three';

// Shared atmosphere state: aerial perspective (haze), sea fog with drifting
// banks, sun in-scattering and cloud shadows. Every lit material in the scene
// is patched to call applyAtmosphere() in place of three's built-in fog, so
// the ships, ocean, particles and sky all fade into the same air.

export const atmoUniforms = {
  uSunDir: { value: new THREE.Vector3(0, 1, 0) },
  /** Direct sun (or moon) irradiance colour at sea level, scene units. */
  uSunColor: { value: new THREE.Color(1, 1, 1) },
  /** Average sky irradiance (for in-scatter of fog / haze). */
  uSkyAmbient: { value: new THREE.Color(0.3, 0.4, 0.5) },
  /** Aerosol extinction at sea level, 1/m. */
  uHaze: { value: 6e-5 },
  /** Sea fog extinction at sea level, 1/m. */
  uFog: { value: 0 },
  /** Scale height of the sea fog, m. */
  uFogHeight: { value: 60 },
  /** Fog bank contrast 0..1. */
  uFogBanks: { value: 0 },
  uFogDrift: { value: new THREE.Vector2() },
  /** Sky cubemap used for directional horizon colour. */
  uSkyCube: { value: null as THREE.Texture | null },
  /** Cloud shadow map (R = transmittance) covering uCloudShadowRect. */
  uCloudShadow: { value: null as THREE.Texture | null },
  /** xy = world centre, z = 1/size, w = strength. */
  uCloudShadowRect: { value: new THREE.Vector4(0, 0, 1 / 12000, 0) },
  /** Lightning: xyz = world position, w = flash intensity. */
  uFlash: { value: new THREE.Vector4(0, 0, 0, 0) },
  uTime: { value: 0 },
};

export const ATMO_GLSL = /* glsl */ `
uniform vec3 uSunDir;
uniform vec3 uSunColor;
uniform vec3 uSkyAmbient;
uniform float uHaze;
uniform float uFog;
uniform float uFogHeight;
uniform float uFogBanks;
uniform vec2 uFogDrift;
uniform samplerCube uSkyCube;
uniform sampler2D uCloudShadow;
uniform vec4 uCloudShadowRect;
uniform vec4 uFlash;

float atmoHash(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}
float atmoNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = atmoHash(i), b = atmoHash(i + vec2(1.0, 0.0));
  float c = atmoHash(i + vec2(0.0, 1.0)), d = atmoHash(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

float atmoCloudShadow(vec3 wp) {
  vec2 uv = (wp.xz - uCloudShadowRect.xy) * uCloudShadowRect.z + 0.5;
  float s = texture(uCloudShadow, uv).r;
  float edge = (1.0 - smoothstep(0.42, 0.5, max(abs(uv.x - 0.5), abs(uv.y - 0.5))));
  return mix(1.0, mix(1.0, s, edge), uCloudShadowRect.w);
}

float atmoPhaseHG(float c, float g) {
  float g2 = g * g;
  return (1.0 - g2) / (12.5663706 * pow(max(1.0 + g2 - 2.0 * g * c, 1e-4), 1.5));
}

// Optical depth of an exponential layer (density d0*exp(-y/H), y >= 0)
// along a ray; rays heading down stop thickening at sea level.
float atmoLayerDepth(float d0, float H, float y0, float dy, float dist) {
  float k = 1.0 / H;
  float yc = max(y0, 0.0);
  float a = d0 * exp(-yc * k);
  if (dy < 0.0) {
    float tSea = yc / max(-dy, 1e-4);
    float t1 = min(dist, tSea);
    float e = k * (-dy) * t1;
    float part = e < 1e-4 ? a * t1 : a * t1 * (exp(e) - 1.0) / e;
    return part + d0 * max(dist - tSea, 0.0);
  }
  float e = k * dy * dist;
  return e < 1e-4 ? a * dist : a * dist * (1.0 - exp(-e)) / e;
}

vec3 atmoInscatterColor(vec3 dir) {
  // The colour of "infinitely far" air in this direction: the sky just above
  // the horizon, which already carries sunset glow and the overcast tint.
  vec3 d = normalize(vec3(dir.x, max(dir.y, 0.02) * 0.6 + 0.03, dir.z));
  vec3 horizon = textureLod(uSkyCube, d, 4.0).rgb;
  return horizon;
}

vec3 applyAtmosphere(vec3 color, vec3 ray) {
  float dist = length(ray);
  if (dist < 1e-3) return color;
  vec3 dir = ray / dist;
  float y0 = cameraPosition.y;
  float haze = atmoLayerDepth(uHaze, 1400.0, y0, dir.y, dist);
  float fogD = uFog;
  if (uFogBanks > 0.0) {
    vec2 mid = cameraPosition.xz + dir.xz * min(dist, 1500.0) * 0.5;
    float n = atmoNoise((mid + uFogDrift) / 520.0) * 0.65 + atmoNoise((mid - uFogDrift * 0.6) / 170.0) * 0.35;
    fogD *= mix(1.0, smoothstep(0.25, 0.75, n) * 1.6 + 0.08, uFogBanks);
  }
  float fog = atmoLayerDepth(fogD, uFogHeight, y0, dir.y, dist);
  float od = haze + fog;
  float T = exp(-od);
  vec3 sky = atmoInscatterColor(dir);
  // Forward scattering toward the sun brightens haze and fog (the "glow").
  float mu = dot(dir, uSunDir);
  vec3 sunGlow = uSunColor * (atmoPhaseHG(mu, 0.72) * 0.9 + 0.02);
  float fogShare = od > 1e-6 ? fog / od : 0.0;
  vec3 fogCol = uSkyAmbient * 0.55 + sunGlow * 0.5;
  vec3 ins = mix(sky + sunGlow * 0.15, fogCol, fogShare);
  ins += vec3(0.75, 0.8, 1.0) * uFlash.w * 0.25;
  return color * T + ins * (1.0 - T);
}
`;

const LIGHT_PATCH_FROM = 'getDirectionalLightInfo( directionalLight, directLight );';
const LIGHT_PATCH_TO = 'getDirectionalLightInfo( directionalLight, directLight );\n\t\tdirectLight.color *= atmoCloudShadow( cameraPosition + vAtmoRay );';

/**
 * Replace three's fog with the shared atmosphere and apply cloud shadows to
 * the sun. `extra` may further edit the shader.
 */
export function patchMaterial<T extends THREE.Material>(mat: T, extra?: (shader: THREE.WebGLProgramParametersWithUniforms) => void, key = ''): T {
  mat.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, atmoUniforms);
    shader.vertexShader = shader.vertexShader
      .replace('#include <fog_pars_vertex>', 'varying vec3 vAtmoRay;')
      .replace('#include <fog_vertex>', 'vAtmoRay = (vec4(mvPosition.xyz, 0.0) * viewMatrix).xyz;');
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <fog_pars_fragment>', ATMO_GLSL + '\nvarying vec3 vAtmoRay;')
      .replace('#include <fog_fragment>', 'gl_FragColor.rgb = applyAtmosphere(gl_FragColor.rgb, vAtmoRay);')
      .replace('#include <lights_fragment_begin>', THREE.ShaderChunk.lights_fragment_begin.replace(LIGHT_PATCH_FROM, LIGHT_PATCH_TO));
    extra?.(shader);
  };
  mat.customProgramCacheKey = () => 'atmo' + key;
  return mat;
}
